import { HandbagIcon } from "@phosphor-icons/react";
import { formatPrice } from "apps/visits/core/utils/format-price";
import { truncate } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toBase64 } from "@/core/shared/v2/common/helpers/to-base64";
import { cn } from "@/core/shared/v2/lib/utils";
import { Button } from "@/core/shared/v2/ui/button";
import { LazyImage } from "@/core/shared/v2/ui/lazy-image";
import { shimmer } from "@/core/shared/v2/ui/shimmer";
import type { BloodTestPackage as BloodTestPackageType } from "../../models";
import { BloodTestsCount } from "../blood-test-package/blood-tests-count";
import { ReadMoreModal } from "../blood-test-package/read-more-modal";

type CatalogItemProps = {
  bloodTestPackage: BloodTestPackageType;
  isSelected?: boolean;
  onSelect: (bloodTestPackage: BloodTestPackageType) => void;
  simplified?: boolean;
  buttonLabel?: string;
};

export const CatalogItem = ({
  bloodTestPackage,
  isSelected = false,
  onSelect,
  simplified = false,
  buttonLabel,
}: CatalogItemProps) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const imageSize = simplified ? 100 : 176;
  const limitExhausted = bloodTestPackage.limitAvailable === false;
  const hasDiscount =
    bloodTestPackage.regularPrice && Number(bloodTestPackage.regularPrice) > Number(bloodTestPackage.grossPrice);

  const openModal = () => setIsModalOpen(true);
  const handleSelect = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.stopPropagation();
    onSelect(bloodTestPackage);
  };

  return (
    <>
      {/* biome-ignore lint/a11y/useSemanticElements: div used to avoid nested buttons (card opens modal, actions inside) */}
      <div
        role="button"
        tabIndex={0}
        data-testid="catalog-item"
        className={cn([
          "flex cursor-pointer flex-col gap-4 rounded-2xl border-2 bg-background p-3 shadow-sm lg:gap-3 lg:p-4",
          { "border-dodger-blue-700": isSelected },
        ])}
        onClick={openModal}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openModal();
          }
        }}
      >
        <div className="flex flex-1 gap-4 lg:gap-5">
          <div
            className={cn("shrink-0", {
              "h-20 w-20 lg:h-[176px] lg:w-[176px]": !simplified,
              "h-20 w-20 lg:h-[100px] lg:w-[100px]": simplified,
            })}
          >
            {bloodTestPackage.imageUrl ? (
              <LazyImage
                src={bloodTestPackage.imageUrl}
                alt={bloodTestPackage.name}
                width={imageSize}
                height={imageSize}
                className="h-full rounded-lg max-lg:size-20"
                placeholder={`data:image/svg+xml;base64,${toBase64(shimmer(imageSize, imageSize))}`}
              />
            ) : (
              <div
                className={cn("rounded-lg bg-gray-100 max-lg:size-20", {
                  "h-[176px] w-[176px]": !simplified,
                  "h-[100px] w-[100px]": simplified,
                })}
              />
            )}
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <div className="flex flex-1 flex-col gap-3 lg:gap-1">
              <div className="font-semibold text-sm lg:text-xl">{bloodTestPackage.name}</div>
              <BloodTestsCount bloodTests={bloodTestPackage.bloodTests} />
              {!simplified && bloodTestPackage.description && (
                <p className="mb-3 hidden text-grey-400 text-sm lg:block">
                  {truncate(bloodTestPackage.description, { length: 128 })}{" "}
                  {bloodTestPackage.description.length > 128 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal();
                      }}
                      className="ml-1 text-foreground hover:underline"
                    >
                      {t("COMMONS.GENERAL.READ_MORE")}
                    </button>
                  )}
                </p>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 pt-1">
              <div className="text-right">
                {limitExhausted ? (
                  <p className="text-grey-500 lg:text-xl">
                    {t("GROUP_ACTIONS_ORDERING.BLOOD_TEST_PACKAGE.LIMIT_AVAILABLE_WARNING")}
                  </p>
                ) : (
                  <p className="font-semibold lg:text-2xl">
                    {Number(bloodTestPackage.grossPrice) > 0
                      ? formatPrice(bloodTestPackage.grossPrice)
                      : t("cmsTranslations.homepage.for_free")}
                  </p>
                )}
                {!limitExhausted && hasDiscount && (
                  <p className="text-muted-foreground text-xs">
                    {t("GROUP_ACTIONS_ORDERING.BLOOD_TEST_PACKAGE.REGULAR_PRICE")}:{" "}
                    {formatPrice(bloodTestPackage.regularPrice)}
                  </p>
                )}
              </div>
              {!limitExhausted && (
                <>
                  <Button
                    arrowOnHover={false}
                    size="lg"
                    onClick={handleSelect}
                    className={cn("max-lg:hidden", { "bg-dodger-blue-700": isSelected })}
                    rightSection={<HandbagIcon size={16} />}
                    data-testid="catalog-add-button"
                  >
                    {isSelected
                      ? t("GROUP_ACTIONS_ORDERING.BLOOD_TEST_PACKAGE.PRODUCT_IN_CART")
                      : buttonLabel || t("GROUP_ACTIONS_ORDERING.BLOOD_TEST_PACKAGE.SELECT_PACKAGE")}
                  </Button>
                  <Button
                    arrowOnHover={false}
                    size="icon"
                    onClick={handleSelect}
                    className={cn("min-h-12 min-w-12 lg:hidden", { "bg-dodger-blue-700": isSelected })}
                    data-testid="catalog-add-button-mobile"
                    aria-label={
                      isSelected
                        ? t("GROUP_ACTIONS_ORDERING.BLOOD_TEST_PACKAGE.PRODUCT_IN_CART")
                        : buttonLabel || t("GROUP_ACTIONS_ORDERING.BLOOD_TEST_PACKAGE.SELECT_PACKAGE")
                    }
                  >
                    <HandbagIcon className="size-6" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <ReadMoreModal
        bloodTestPackage={bloodTestPackage}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onAddToCart={() => onSelect(bloodTestPackage)}
        isProductInCart={isSelected}
        buttonLabel={buttonLabel}
      />
    </>
  );
};
